import type { Event as SentryEvent, Breadcrumb } from '@sentry/types';
import { Scope } from '@sentry/core';
import {
  BrowserClient,
  defaultStackParser,
  makeFetchTransport,
} from '@sentry/browser';

type CreateWidgetSentryParams = {
  dsn?: string;
  environment?: string;
  release?: string;
  sessionId: string;
  getCurrentWidget: () => string | null;
  getAgentBrand: () => string | null;
};

export type WidgetSentry = {
  captureException: (error: unknown, extra?: Record<string, unknown>) => void;
  captureMessage: (
    message: string,
    level?: 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug'
  ) => void;
  addBreadcrumb: (crumb: Breadcrumb) => void;
  setTag: (key: string, value: string) => void;
  withScope: (cb: (scope: Scope) => void) => void;
};

function sanitizeEvent(evt: SentryEvent): SentryEvent {
  const clone: SentryEvent = { ...evt };
  if (clone.breadcrumbs)
    clone.breadcrumbs = clone.breadcrumbs.map((b) => ({
      ...b,
      data: b.data,
    }));
  return clone;
}

export function createWidgetSentry(
  params: CreateWidgetSentryParams
): WidgetSentry | null {
  const {
    dsn,
    environment,
    release,
    sessionId,
    getAgentBrand,
    getCurrentWidget,
  } = params;

  if (!dsn) {
    return null;
  }

  const client = new BrowserClient({
    dsn,
    environment,
    release,
    stackParser: defaultStackParser,
    transport: makeFetchTransport,
    integrations: [],
    beforeSend(event) {
      return sanitizeEvent(event);
    },
    beforeBreadcrumb(crumb) {
      return crumb;
    },
    sampleRate: 1.0,
    tracesSampleRate: 0,
  });

  const baseTags: Record<string, string> = {
    app: 'frontend-widgets',
    ...(environment ? { environment } : {}),
    ...(release ? { release } : {}),
  };
  const dynamicTags: Record<string, string> = {};
  const breadcrumbsBuffer: Breadcrumb[] = [];

  function buildScope(extra?: Record<string, unknown>): Scope {
    const scope = new Scope();
    Object.entries(baseTags).forEach(([k, v]) => scope.setTag(k, v));
    Object.entries(dynamicTags).forEach(([k, v]) => scope.setTag(k, v));
    scope.setUser({ id: sessionId });
    try {
      const origin =
        typeof window !== 'undefined' ? window.location.origin : '';
      if (origin) scope.setTag('host_origin', origin);
    } catch (_) {}
    if (extra) scope.setExtras(extra);
    breadcrumbsBuffer.forEach((crumb) => scope.addBreadcrumb(crumb));
    return scope;
  }

  function captureException(error: unknown, extra?: Record<string, unknown>) {
    const scope = buildScope(extra);
    const widget = getCurrentWidget();
    const agentBrand = getAgentBrand();
    if (widget) scope.setTag('widget', widget);
    if (agentBrand) scope.setTag('agent_brand', agentBrand);
    client.captureException(error as any, undefined, scope);
  }

  function captureMessage(
    message: string,
    level: 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug' = 'error'
  ) {
    const scope = buildScope();
    const widget = getCurrentWidget();
    const agentBrand = getAgentBrand();
    if (widget) scope.setTag('widget', widget);
    if (agentBrand) scope.setTag('agent_brand', agentBrand);
    client.captureMessage(message, level, undefined, scope);
  }

  function addBreadcrumb(crumb: Breadcrumb) {
    try {
      breadcrumbsBuffer.push(crumb);
    } catch (_) {}
  }

  function setTag(key: string, value: string) {
    dynamicTags[key] = value;
  }

  function withScope(cb: (scope: Scope) => void) {
    cb(buildScope());
  }

  return {
    captureException,
    captureMessage,
    addBreadcrumb,
    setTag,
    withScope,
  };
}

export function captureExceptionSafe(
  error: unknown,
  extra?: Record<string, unknown>
) {
  const sentry = (window as any).gordianInternal?._sentry as
    | WidgetSentry
    | undefined;
  sentry?.captureException(error, extra);
}

export function captureMessageSafe(
  message: string,
  level?: 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug'
) {
  const sentry = (window as any).gordianInternal?._sentry as
    | WidgetSentry
    | undefined;
  sentry?.captureMessage(message, level);
}

export function addBreadcrumbSafe(crumb: Breadcrumb) {
  const sentry = (window as any).gordianInternal?._sentry as
    | WidgetSentry
    | undefined;
  sentry?.addBreadcrumb(crumb);
}
